// UI strings for Oppitunnit as the week hub (docs/design/SIVUPALSTA.md "Oppitunnit (week hub)"):
// the current week with its step ribbon (Tunti · Harjoitus · Kuuntele · Puhu · Kirje · Yhdessä),
// the table of weeks, the steps' own pages under the lesson (/lessons/:id/listen …) and their
// "Seuraavaksi" lines. Step names themselves are t.course.week.steps (shared with Tänään).
// Finnish follows docs/FINNISH-STYLE.md: te-muoto for both, no jargon, only actionable text.
import { defineArea } from '../area';

export const weekHubFi = {
  /** The week's kicker: "Viikko 3 · avoinna". */
  week: (n: number) => `Viikko ${n}`,
  /** The ribbon as a landmark. */
  ribbon: 'Viikon askeleet',
  /** The primary action under the ribbon: the first step not done yet. */
  continueTo: (step: string) => `Jatka: ${step}`,
  /** Every step of the week done (the ones that record anything). */
  weekDone: 'Viikon askeleet on tehty.',
  next: (what: string) => `Seuraavaksi: ${what}`,
  /** The week after this one, as the last step's next line. */
  nextWeek: (n: number, date: string) => `oppitunti ${n} avautuu ${date}`,
  nextWeekWaits: (n: number) => `oppitunti ${n}, kun olette molemmat läpäisseet tämän`,
  courseDone: 'kurssi on käyty läpi',
  /** A passed week's door (the week's own practice: another page). */
  practiceWeek: (n: number) => `Harjoittele viikkoa ${n}`,
  /** Row states (the aligned note on PC, the row's end on the phone). */
  state: {
    passed: 'läpäisty',
    open: 'avoinna',
    coming: (date: string) => `avautuu ${date}`,
    waits: (n: number) => `avautuu, kun olette molemmat läpäisseet oppitunnin ${n}`,
  },
  /** Who has passed: "sinä ✓ · Roosa ✓" (the marks are drawn; these are the words). */
  you: 'sinä',
  passedBy: (who: string) => `${who} läpäisi`,
  notYet: (who: string) => `${who}: ei vielä`,
  /** Phone disclosures under the current week. */
  passedH: (n: number) => `Läpäistyt ${n}`,
  comingH: (n: number) => `Tulevat ${n}`,
  /** The table of weeks (PC) as a heading for screen readers. */
  allWeeks: 'Kaikki viikot',
  /** Before the course starts: the only line on the page, with Kielioppi as the action. */
  notStarted: (date: string) => `Ensimmäinen oppitunti avautuu ${date}.`,
  readGrammar: 'Lue kielioppia',
  /** 1920 lane B: the selected week's outline. */
  outline: {
    words: 'Sanat',
    duolingo: 'Duolingo',
    grammar: 'Kielioppi',
    open: 'Avaa',
    more: (n: number) => `+ ${n} muuta`,
  },
  /** The steps' pages (/lessons/:id/<step>). */
  steps: {
    cards: {
      done: 'Päivän kortit on tehty tänään.',
      todo: 'Päivän kortit odottavat.',
      start: 'Päivän kortit',
      /** A past week's Harjoitus: that week's own cards (Harjoittele viikkoa N). */
      week: (n: number) => `Viikon ${n} kortit`,
    },
    listen: {
      play: 'Kuuntele',
      record: 'Äänitä',
      /** The active line's tool in the margin: record and compare with Peterson. */
      tool: 'Äänitä ja vertaa',
      none: 'Tällä oppitunnilla ei ole sarjan repliikkejä.',
      browse: 'Sarjan repliikit',
    },
    speak: {
      /** A door to the week's lines (listened and recorded on Kuuntele). */
      lines: (n: number) => `Viikon repliikit (${n}): kuuntele ja äänitä`,
      none: 'Tällä viikolla ei ole ääntämisharjoitusta.',
    },
    letter: {
      none: 'Tällä viikolla ei ole kirjetehtävää.',
      open: 'Kirjeet',
    },
    together: {
      /** The step ticks with a scene take of yours (course/week.ts), so it opens the scene. */
      open: 'Kohtaus roolipelinä',
      session: 'Yhteinen sessio',
    },
  },
};

export const weekHubEn: typeof weekHubFi = {
  week: (n) => `Week ${n}`,
  ribbon: 'The week’s steps',
  continueTo: (step) => `Continue: ${step}`,
  weekDone: 'This week’s steps are done.',
  next: (what) => `Next: ${what}`,
  nextWeek: (n, date) => `lesson ${n} opens ${date}`,
  nextWeekWaits: (n) => `lesson ${n}, once you have both passed this one`,
  courseDone: 'the course is complete',
  practiceWeek: (n) => `Practise week ${n}`,
  state: {
    passed: 'passed',
    open: 'open',
    coming: (date) => `opens ${date}`,
    waits: (n) => `opens once you have both passed lesson ${n}`,
  },
  you: 'you',
  passedBy: (who) => `${who} passed`,
  notYet: (who) => `${who}: not yet`,
  passedH: (n) => `Passed ${n}`,
  comingH: (n) => `Coming ${n}`,
  allWeeks: 'All weeks',
  notStarted: (date) => `The first lesson opens ${date}.`,
  readGrammar: 'Read the grammar',
  outline: {
    words: 'Words',
    duolingo: 'Duolingo',
    grammar: 'Grammar',
    open: 'Open',
    more: (n) => `+ ${n} more`,
  },
  steps: {
    cards: {
      done: 'Today’s cards are done.',
      todo: 'Today’s cards are waiting.',
      start: 'Today’s cards',
      week: (n) => `Week ${n} cards`,
    },
    listen: {
      play: 'Listen',
      record: 'Record',
      tool: 'Record and compare',
      none: 'This lesson has no lines from the shows.',
      browse: 'Lines from the shows',
    },
    speak: {
      lines: (n) => `The week’s lines (${n}): listen and record`,
      none: 'This week has no pronunciation practice.',
    },
    letter: {
      none: 'This week has no letter to write.',
      open: 'Letters',
    },
    together: {
      open: 'Scene as role play',
      session: 'Shared session',
    },
  },
};

// Messages.weekHub (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('weekHub', weekHubFi, weekHubEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof weekHubFi> {}
}
